// Copyright ©️ 2026 eVolve MEP, LLC

import { format, isValid, parseISO } from 'date-fns';

declare const dateOnlySymbol: unique symbol;
/** `YYYY-MM-DD` */
export type DateOnly = string & { [dateOnlySymbol]: never };

export type DateRange = [DateOnly | null, DateOnly | null];

/**
 * Converts a `Date` object or an `ISOTimestamp` to a `DateOnly`.
 */
export const dateOnly = (date: Date | ISOTimestamp): DateOnly =>
  format(typeof date === 'string' ? parseISO(date) : date, 'yyyy-MM-dd') as DateOnly;

export const isDateOnly = (dateString: string): dateString is DateOnly => {
  if (!/\d{4}-\d{2}-\d{2}/.test(dateString)) return false;
  const dateObj = parseISO(dateString);
  return isValid(dateObj) && dateOnly(dateObj) === dateString;
};

export const today = (): DateOnly => dateOnly(new Date());

declare const isoTimestampSymbol: unique symbol;
/**
 * `YYYY-MM-DDTHH:MM:SS[.MMMMMM]X`
 *
 * where `X` is Timezone
 */
export type ISOTimestamp = string & { [isoTimestampSymbol]: never };

/**
 * Converts a `Date` object to an `ISOTimestamp`.
 */
export const isoTimestamp = (date: Date): ISOTimestamp => date.toISOString() as ISOTimestamp;

export const formatDate = (
  date: DateOnly | ISOTimestamp | Date | null | undefined,
  formatStr: Parameters<typeof format>[1],
  fallback = '--',
): string => {
  if (!date) return fallback;
  const dateObj = typeof date === 'object' ? date : parseISO(date);
  if (!isValid(dateObj)) return fallback;
  return format(dateObj, formatStr);
};

export const formatDateRange = (dateRange: DateRange | [Date | null, Date | null]) =>
  `${formatDate(dateRange[0], 'MM/dd/yyyy', '')} — ${formatDate(dateRange[1], 'MM/dd/yyyy', '')}`;
